import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from 'lucide-react';

/**
 * Pagination over the in-memory list.
 *
 * Not `common/Pagination/PaginationComponent`: that one drives a server round-trip per page and
 * carries the react-js-pagination markup this page's styling would have to fight. Paging here is
 * a slice of an array already in state.
 *
 * Page numbers are windowed rather than all rendered — a camp with 200 coupons is 14 pages, but
 * the window keeps the control a fixed width whatever the count.
 */
const WINDOW = 2;

const buildPages = (page, totalPages) => {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = [1];
    const from = Math.max(2, page - WINDOW);
    const to = Math.min(totalPages - 1, page + WINDOW);

    if (from > 2) pages.push('start-gap');
    for (let i = from; i <= to; i += 1) pages.push(i);
    if (to < totalPages - 1) pages.push('end-gap');

    pages.push(totalPages);
    return pages;
};

const CouponPagination = ({ page, totalPages, rangeStart, rangeEnd, totalItems, onChange }) => {
    const [goTo, setGoTo] = useState('');

    const go = next => onChange(Math.min(Math.max(next, 1), totalPages));

    const handleGo = event => {
        event.preventDefault();
        const parsed = Number(goTo);
        if (Number.isFinite(parsed) && parsed >= 1 && parsed <= totalPages) {
            go(parsed);
            setGoTo('');
        }
    };

    // Shared by the arrows and the numbers so the two are the same box by construction.
    // `flex` + `justify-center` is doing real work here, not just centring an icon: the page-level
    // reset sets `button { text-align: inherit }`, so a fixed-width button with text in it inherits
    // the page's left alignment and the digit sits against the left edge of its 36px square.
    const boxClass =
        'cpn-flex cpn-h-9 cpn-w-9 cpn-items-center cpn-justify-center cpn-rounded-lg cpn-transition';

    const arrowClass = `${boxClass} cpn-border cpn-border-neutral-200 cpn-bg-white cpn-text-neutral-600 hover:cpn-border-brand-300 hover:cpn-text-brand-700 disabled:cpn-cursor-not-allowed disabled:cpn-opacity-40 disabled:hover:cpn-border-neutral-200 disabled:hover:cpn-text-neutral-600`;

    const pageClass = active =>
        `${boxClass} cpn-text-sm cpn-font-semibold ${
            active
                ? 'cpn-bg-brand-700 cpn-text-white'
                : 'cpn-border cpn-border-neutral-200 cpn-bg-white cpn-text-neutral-600 hover:cpn-border-brand-300 hover:cpn-text-brand-700'
        }`;

    return (
        // `1fr auto 1fr` rather than `justify-between`: the count on the left and the go-to form on
        // the right are different widths, so space-between would push the numbers off-centre. Equal
        // fr columns either side centre them on the card regardless of what those two contain.
        <nav
            aria-label="Pagination"
            className="cpn-flex cpn-flex-col cpn-items-center cpn-gap-4 cpn-border-t cpn-border-neutral-200 cpn-px-4 cpn-py-5 sm:cpn-grid sm:cpn-grid-cols-[1fr_auto_1fr] sm:cpn-items-center sm:cpn-px-6"
        >
            <p className="cpn-text-sm cpn-text-neutral-500 sm:cpn-justify-self-start">
                Showing{' '}
                <span className="cpn-font-semibold cpn-text-neutral-900">
                    {rangeStart}–{rangeEnd}
                </span>{' '}
                of <span className="cpn-font-semibold cpn-text-neutral-900">{totalItems}</span>{' '}
                {totalItems === 1 ? 'coupon' : 'coupons'}
            </p>

            <div className="cpn-flex cpn-items-center cpn-gap-1.5 sm:cpn-justify-self-center">
                <button
                    type="button"
                    className={arrowClass}
                    onClick={() => go(1)}
                    disabled={page === 1}
                    aria-label="First page"
                >
                    <ChevronsLeft className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className={arrowClass}
                    onClick={() => go(page - 1)}
                    disabled={page === 1}
                    aria-label="Previous page"
                >
                    <ChevronLeft className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                </button>

                {buildPages(page, totalPages).map(item =>
                    typeof item === 'string' ? (
                        <span key={item} className="cpn-px-1 cpn-text-sm cpn-text-neutral-400">
                            …
                        </span>
                    ) : (
                        <button
                            key={item}
                            type="button"
                            onClick={() => go(item)}
                            aria-current={item === page ? 'page' : undefined}
                            className={pageClass(item === page)}
                        >
                            {item}
                        </button>
                    )
                )}

                <button
                    type="button"
                    className={arrowClass}
                    onClick={() => go(page + 1)}
                    disabled={page === totalPages}
                    aria-label="Next page"
                >
                    <ChevronRight className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className={arrowClass}
                    onClick={() => go(totalPages)}
                    disabled={page === totalPages}
                    aria-label="Last page"
                >
                    <ChevronsRight className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                </button>
            </div>

            <form
                className="cpn-flex cpn-items-center cpn-gap-2 sm:cpn-justify-self-end"
                onSubmit={handleGo}
            >
                <label className="cpn-sr-only" htmlFor="go-to-page">
                    Go to page
                </label>
                <div className="cpn-relative">
                    <Search
                        className="cpn-pointer-events-none cpn-absolute cpn-left-3 cpn-top-1/2 cpn-h-4 cpn-w-4 -cpn-translate-y-1/2 cpn-text-neutral-400"
                        aria-hidden="true"
                    />
                    <input
                        id="go-to-page"
                        value={goTo}
                        onChange={event => setGoTo(event.target.value)}
                        inputMode="numeric"
                        placeholder="Go to page"
                        className="cpn-h-9 cpn-w-36 cpn-rounded-lg cpn-border cpn-border-neutral-200 cpn-bg-white cpn-pl-9 cpn-pr-3 cpn-text-sm cpn-text-neutral-900 placeholder:cpn-text-neutral-400 focus:cpn-border-brand-500 focus:cpn-outline-none focus:cpn-ring-2 focus:cpn-ring-brand-100"
                    />
                </div>
                <button
                    type="submit"
                    className="cpn-h-9 cpn-rounded-lg cpn-bg-brand-700 cpn-px-4 cpn-text-sm cpn-font-semibold cpn-text-white cpn-transition hover:cpn-bg-brand-800"
                >
                    Go
                </button>
            </form>
        </nav>
    );
};

export default CouponPagination;
